import Enums from "@/plugins/Enums.js"

const VdpStatus = Enums.Vdpstatus

const enumValue = v => Number(v?.value ?? v) || 0
const str = v => String(v ?? "").trim()
const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0)

const numText = v => {
	const n = num(v)
	return n.toFixed(6).replace(/\.?0+$/, "")
}

export const parseParentKey = parentKey => {
	const [identRaw, localKey] = String(parentKey || "").split(":")
	return {
		ident: Number(identRaw) || 0,
		localKey,
	}
}

const buildChild = (parent, type, qty) => {
	const t = enumValue(type)

	const safeQty = Math.max(0, num(qty))
	if (safeQty <= 0) return null

	const parentInsumo = str(parent?.vdpinsumo)
	const parentRecip = str(parent?.vdprecip)

	const child = {
		type: t,
		qty: safeQty,
		vdpinsumo: "",
		vdprecip: "",
	}

	const isInsumoChild = t === VdpStatus.Sale.value || t === VdpStatus.Return.value
	if (isInsumoChild) child.vdpinsumo = parentInsumo
	else child.vdprecip = parentRecip

	return child
}

const addOrIncrementChild = (children, parent, type, qty) => {
	const t = enumValue(type)
	const safeQty = Math.max(0, num(qty))
	if (safeQty <= 0) return

	const list = Array.isArray(children) ? children : []
	const existing = list.find(c => enumValue(c?.type) === t)

	if (!existing) {
		const next = buildChild(parent, t, safeQty)
		if (next) list.push(next)
		return
	}

	const sum = num(existing.qty) + safeQty
	existing.qty = Math.max(0, Number.isFinite(sum) ? sum : safeQty)
}

export const computeBaseChildren = parent => {
	const children = []

	const insumo = str(parent?.vdpinsumo)
	const recip = str(parent?.vdprecip)

	const ident = num(parent?.vdpident) || 0

	const qty = Math.max(0, num(parent?.vdpqtde))

	const capRaw = num(parent?.in9capac)
	const capacity = Number.isFinite(capRaw) && capRaw > 0 ? capRaw : 1

	const cylinders = insumo ? (qty > 0 ? Math.ceil(qty / capacity) : 0) : qty

	if (ident === 0) {
		if (insumo) {
			const c = buildChild(parent, VdpStatus.Sale.value, qty)
			if (c) children.push(c)
		}
		if (recip) {
			const c = buildChild(parent, VdpStatus.Loan.value, cylinders)
			if (c) children.push(c)
		}
		return children
	}

	if (insumo) {
		const c = buildChild(parent, VdpStatus.Return.value, qty)
		if (c) children.push(c)
	}
	if (recip) {
		const c = buildChild(parent, VdpStatus.LoanReturn.value, cylinders)
		if (c) children.push(c)
	}

	return children
}

export const normalizeChildren = children =>
	(children || [])
		.filter(c => {
			const q = num(c?.qty)
			return Number.isFinite(q) && q > 0
		})
		.map(c => ({
			type: enumValue(c?.type),
			qty: Math.max(0, num(c?.qty)),
			vdpinsumo: str(c?.vdpinsumo),
			vdprecip: str(c?.vdprecip),
		}))

export const childrenSignature = children => {
	const out = []
	for (const c of children || []) {
		const type = enumValue(c?.type)
		const insumo = str(c?.vdpinsumo)
		const recip = str(c?.vdprecip)
		const qtyText = numText(c?.qty)
		out.push(`${type}|${qtyText}|${insumo}|${recip}`)
	}
	return out.join("~")
}

export const recipGroupSignature = (parentKeys, itemsByParentKey) => {
	const keys = Array.isArray(parentKeys) ? [...parentKeys].sort() : []

	const out = []
	for (const pk of keys) {
		const item = itemsByParentKey.get(pk)
		if (!item) {
			out.push(`${pk}|missing`)
			continue
		}

		const recip = str(item?.vdprecip)
		const insumo = str(item?.vdpinsumo)

		const ident = num(item?.vdpident) || 0

		const qty = Math.max(0, num(item?.vdpqtde))

		const capRaw = num(item?.in9capac)
		const capacity = Number.isFinite(capRaw) && capRaw > 0 ? capRaw : 1
		const cylinders = insumo ? (qty > 0 ? Math.ceil(qty / capacity) : 0) : qty

		const qtyText = numText(qty)
		const cylText = numText(cylinders)
		const capText = numText(capRaw)

		out.push([pk, recip, insumo, qtyText, cylText, ident, capText].join("|"))
	}

	return out.join("§")
}

export const indexItems = (soldMap, returnedMap) => {
	const itemsByParentKey = new Map()
	const parentKeysByRecip = new Map()

	for (const map of [soldMap, returnedMap]) {
		for (const item of (map || new Map()).values()) {
			const pk = str(item?.parentKey)
			if (!pk) continue

			itemsByParentKey.set(pk, item)

			const recip = str(item?.vdprecip)
			if (!recip) continue

			const list = parentKeysByRecip.get(recip)
			if (list) list.push(pk)
			else parentKeysByRecip.set(recip, [pk])
		}
	}

	return { itemsByParentKey, parentKeysByRecip }
}

export const computeRecipGroupChildren = (parentKeys, itemsByParentKey) => {
	const keys = Array.isArray(parentKeys) ? [...parentKeys].sort() : []

	const childrenByParentKey = new Map()
	const baseChildrenByParentKey = new Map()

	for (const pk of keys) {
		const parent = itemsByParentKey.get(pk)
		if (!parent) continue
		const base = computeBaseChildren(parent)
		baseChildrenByParentKey.set(pk, base)
		childrenByParentKey.set(pk, base)
	}

	const soldComodato = []
	const returnedComodato = []

	for (const pk of keys) {
		const parent = itemsByParentKey.get(pk)
		const base = baseChildrenByParentKey.get(pk)
		if (!parent || !base) continue

		const ident = num(parent?.vdpident) || 0

		if (ident === 0) {
			const child = base.find(x => enumValue(x?.type) === VdpStatus.Loan.value)
			if (child && num(child.qty) > 0) soldComodato.push({ pk, parent, child })
		} else {
			const child = base.find(x => enumValue(x?.type) === VdpStatus.LoanReturn.value)
			if (child && num(child.qty) > 0) returnedComodato.push({ pk, parent, child })
		}
	}

	let soldIndex = 0
	let returnedIndex = 0

	while (soldIndex < soldComodato.length && returnedIndex < returnedComodato.length) {
		const sold = soldComodato[soldIndex]
		const ret = returnedComodato[returnedIndex]

		const soldQty = Math.max(0, num(sold.child.qty))
		const retQty = Math.max(0, num(ret.child.qty))

		if (soldQty <= 0) {
			soldIndex++
			continue
		}
		if (retQty <= 0) {
			returnedIndex++
			continue
		}

		const matched = Math.min(soldQty, retQty)
		if (matched <= 0) break

		const soldChildren = childrenByParentKey.get(sold.pk) || []
		const retChildren = childrenByParentKey.get(ret.pk) || []

		addOrIncrementChild(soldChildren, sold.parent, VdpStatus.FreeExchange.value, matched)
		addOrIncrementChild(retChildren, ret.parent, VdpStatus.FreeExchange.value, matched)

		sold.child.qty = Math.max(0, soldQty - matched)
		ret.child.qty = Math.max(0, retQty - matched)

		childrenByParentKey.set(sold.pk, soldChildren)
		childrenByParentKey.set(ret.pk, retChildren)

		if (num(sold.child.qty) <= 0) soldIndex++
		if (num(ret.child.qty) <= 0) returnedIndex++
	}

	for (const [pk, children] of childrenByParentKey.entries()) {
		childrenByParentKey.set(pk, normalizeChildren(children))
	}

	return childrenByParentKey
}
